import { Observable, Subject, scan } from "rxjs";

export enum AlertType {
	Success = "success",
	Error = "error",
	Info = "info",
	Action = "action",

	/**
	 * @deprecated use any other type with an `image` value instead.
	 */
	Product = "product"
}

export interface Alert {
	type: AlertType,
	title: string,
	message?: string,
	image?: string,
	button?: string,
	onDismiss?: string | (() => void),
}

export interface Toast extends Alert {
	id: number,
	delete?: boolean
}

const autoHideTime = 2000;

let count = 0; // used for toast ID

const subject$: Subject<Toast> = new Subject();

const toasts$: Observable<Toast[]> = subject$.pipe(
	scan((list: Toast[], toast) => {
		if (toast.delete) {
			const index = list.findIndex((item) => item.id === toast.id);
			list.splice(index, 1);
			return list;
		}
		return [...list, toast];
	}, []),
);


/**
 * Adds a toast message to the top right of the app
 * @depricated use `toastSuccess`, `toastInfo`, `toastError`, or `toastAction` instead.
 * @param alert use 'title' as primary field, can also add 'message'
 * @param dismissable if not dismissable will dismiss after 5 seconds
 */
export function toast(alert: Alert, dismissable = false) {
	const id = count;
	count++;
	subject$.next({
		...alert,
		id,
		onDismiss: dismissable ? () => subject$.next({ ...alert, id, delete: true }) : undefined,
	});
	if (!dismissable) setTimeout(() => subject$.next({ ...alert, id, delete: true }), autoHideTime);
}

/**
 * `toast` but with a dynamically settable auto-hide time.
 * @param alert Alert title, optional message, option image, button text, and onDismiss
 * action.
 * @param autoHideMs Milliseconds to wait before automatically dismissing the toast. If
 * 0, the user must dismiss it.
 */
function _toast(alert: Alert, autoHideMs = 0) {
	const clone = { ...alert, id: count++ };
	const dismiss = () => subject$.next({ ...clone, delete: true });
	subject$.next({ ...clone, onDismiss: autoHideMs > 0 ? undefined : dismiss });
	if (autoHideMs > 0) setTimeout(dismiss, autoHideMs);
}

export function toastSuccess(
	title: Alert['title'],
	autoHide = true,
	message?: Alert['message'],
	image?: Alert['image'],
) {
	_toast({ type: AlertType.Success, title, message, image }, autoHide ? autoHideTime : 0)
}

export function toastInfo(
	title: Alert['title'],
	autoHide = true,
	message?: Alert['message'],
	image?: Alert['image'],
) {
	_toast({ type: AlertType.Info, title, message, image }, autoHide ? autoHideTime : 0)
}

export function toastError(
	title: Alert['title'],
	autoHide = true,
	message?: Alert['message'],
	image?: Alert['image'],
) {
	_toast({ type: AlertType.Error, title, message, image }, autoHide ? autoHideTime : 0)
}

export function toastAction(
	title: string,
	autoHide = true,
	alert: Pick<Alert, 'message' | 'image' | 'button' | 'onDismiss'> = {},
) {
	_toast({ type: AlertType.Action, title, ...alert }, autoHide ? autoHideTime : 0)
}


/**
 * @depricated use `toastSuccess`, `toastInfo`, `toastError`, or `toastAction` instead.
 * Enqueue a message to display as a toast
 * @param message - The text to display in the toast
 * @param type - What kind of message it is (e.g. success, error)
 * @param time - How long the toast should show
 * @param iconUrl - The product to display in the toast
 */
export function setToastMessage(message: string, type: AlertType = AlertType.Info, time?: number, iconUrl?: string) {
	toast({
		type,
		title: message,
		image: iconUrl
	});
}

export function streamToastMessages() {
	return toasts$;
}